import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../centrui/src/app/services/api.service';
import { ModalService } from '../../centrui/src/app/services/modal.service';
import { LoadingService } from '../../centrui/src/app/services/loading.service';
import { NotifierService } from '../../centrui/src/app/services/notifier.service';
import { MessageType } from '../../centrui/src/app/constants/MessageType';
import { ResourceType } from '../../centrui/src/app/constants/ResourceType';
import { NgxSmartModalComponent } from '../../centrui/src/app/impl/NgxSmartModalComponent';
import { Response } from '../../centrui/src/app/models/data/Response';
import { Attachment } from '../models/Attachment';
import { SocketService } from '../../services/socket.service';
import { SavePresentationNameComponent } from '../../components/modals/save-presentation-name/save-presentation-name.component';
import {
  PresentationSaveRequest,
  PresentationSaveResult,
  PresentationSaveService,
} from '../../lumi-slides/src/app/features/presentation-builder/services/presentation-save.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PolarixPresentationSaveService implements PresentationSaveService {
  private readonly apiService = inject(ApiService);
  private readonly httpClient = inject(HttpClient);
  private readonly modalService = inject(ModalService);
  private readonly socketService = inject(SocketService);
  private readonly injector = inject(Injector);
  private readonly loadingService = inject(LoadingService);
  private readonly notifierService = inject(NotifierService);

  async save(request: PresentationSaveRequest): Promise<PresentationSaveResult | null> {
    let name = request.title.trim();
    const isNew = request.attachmentId == null;

    if (isNew) {
      const prompted = await this.promptForName(name || 'Untitled presentation');
      if (!prompted) {
        return null;
      }
      name = prompted;
    }

    this.loadingService.setLoading('Saving presentation...');

    try {
      const [{ PptxExportService }, { JsonImportService }] = await Promise.all([
        import('../../lumi-slides/src/app/features/presentation-builder/services/pptx-export.service'),
        import('../../lumi-slides/src/app/features/presentation-builder/services/json-import.service'),
      ]);
      const filename = `${name}.pptx`;
      const blob = await this.injector.get(PptxExportService).toBlob(request.slides, filename);
      const file = new File([blob], filename, {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const presentationData = JSON.stringify(this.injector.get(JsonImportService).exportData(request.slides));

      let attachment: Attachment;
      if (isNew) {
        attachment = (await firstValueFrom(
          this.apiService.post(
            Attachment,
            this.buildCreateFormData(name, request.projectId, file, presentationData),
            null,
            undefined,
            true,
          ),
        )) as Attachment;
        this.socketService.emitAttachmentCreated(attachment);
      } else {
        attachment = await firstValueFrom(
          this.httpClient
            .post(
              `${environment.apiUrl}/${Attachment.resource}/${request.attachmentId}`,
              this.buildUpdateFormData(name, request.projectId, file, presentationData),
            )
            .pipe(map((response: unknown) => new Attachment(new Response(response as object).data))),
        );
        this.socketService.emitAttachmentUpdated(attachment);
      }

      this.notifierService.notify(MessageType.SUCCESS, 'Presentation saved successfully.');

      return {
        attachmentId: attachment.id,
        name: attachment.name,
      };
    } finally {
      this.loadingService.setLoading();
    }
  }

  private buildCreateFormData(
    name: string,
    projectId: number,
    file: File,
    presentationData: string,
  ): FormData {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('project_id', String(projectId));
    formData.append('type', ResourceType.PPTX);
    formData.append('presentation_data', presentationData);
    formData.append('file[]', file);
    return formData;
  }

  private buildUpdateFormData(
    name: string,
    projectId: number,
    file: File,
    presentationData: string,
  ): FormData {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('project_id', String(projectId));
    formData.append('type', ResourceType.PPTX);
    formData.append('presentation_data', presentationData);
    formData.append('file', file);
    formData.set('_method', 'PATCH');
    return formData;
  }

  private promptForName(defaultName: string): Promise<string | null> {
    return firstValueFrom(
      this.modalService.open(
        SavePresentationNameComponent,
        { defaultName },
        { customClass: 'nsm-centered nsm-above-presentation-builder' },
      ),
    ).then((response: NgxSmartModalComponent) => {
      if (!response.hasData()) {
        return null;
      }
      const data = response.getData().data as { name?: string } | undefined;
      const trimmed = data?.name?.trim() ?? '';
      return trimmed || null;
    });
  }
}
